import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

/**
 * Amazon Federate OIDC callback page.
 *
 * Amazon redirects here with ?code=...&state=... after the user
 * authenticates. We exchange the code for tokens via our backend.
 */
export default function AmazonCallback() {
  const [searchParams] = useSearchParams();
  const { handleAmazonCallback } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (errorParam) {
      setError(`Amazon login failed: ${errorParam}`);
      return;
    }

    if (!code || !state) {
      setError("Missing authorization code or state");
      return;
    }

    handleAmazonCallback(code, state)
      .then(() => {
        // Check if admin or regular user and redirect accordingly
        const token = localStorage.getItem("tv_token");
        if (token) {
          try {
            const decoded = JSON.parse(atob(token.split(".")[1]));
            navigate(decoded.is_admin ? "/admin" : "/dashboard", { replace: true });
          } catch {
            navigate("/dashboard", { replace: true });
          }
        } else {
          // 2FA required — redirect to login for TOTP entry
          navigate("/login", { replace: true });
        }
      })
      .catch((err) => {
        setError(err.message || "Authentication failed");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-900">
        <div className="max-w-md rounded-xl bg-surface-700 border border-border p-8 text-center">
          <h2 className="text-h4 text-health-400 mb-4">Login Failed</h2>
          <p className="text-body-sm text-text-secondary mb-6">{error}</p>
          <a href="/login" className="btn-primary px-6 py-2">
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-900">
      <div className="flex items-center gap-3">
        <div className="w-6 h-6 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-body text-text-primary">Completing Amazon login...</span>
      </div>
    </div>
  );
}
